import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './HowItWorks.css';

const chartFrameCandidates = ['/test_chart_75.png', '/test_chart_150.png', '/test_chart_300.png'];
const chartLabels = ['75 entries', '150 entries', '300 entries'];

type ChatMessage = { from: 'user' | 'coach'; text: string };

type FeatureBlock = {
  id: string;
  title: string;
  copy: string;
  messages: ChatMessage[];
  systemNote: string;
};

const featureBlocks: FeatureBlock[] = [
  {
    id: 'log',
    title: 'Log meals, workouts, and mood',
    copy:
      "Describe what you ate, how your workout went, or how you're feeling. mosaime extracts the details automatically — calories, macros, workout metrics, sleep quality — and gives you a coaching response back. Every entry builds your Wellness Mosaic and sharpens your coach's understanding of your patterns.",
    messages: [
      { from: 'user', text: 'Made scrambled eggs and toast for breakfast. Little bit of butter.' },
      { from: 'coach', text: 'Good protein anchor to start the day. Did you have anything to drink — coffee, juice?' },
      { from: 'user', text: 'Just black coffee.' },
    ],
    systemNote: 'Meal logged · 420 cal · protein 18g · carbs 32g · fat 22g · NOVA: minimally processed',
  },
  {
    id: 'scan',
    title: 'Scan ingredient labels',
    copy:
      'Send a photo of any food product\'s ingredient label and mosaime breaks it down: processing level, ultra-processed flags, detection of misleading claims (like "natural" on a heavily processed item), nutrition highlights, and cleaner swap suggestions.',
    messages: [
      { from: 'user', text: '[photo of a protein bar ingredient label]' },
      { from: 'coach', text: "This one's more processed than it looks — glucose syrup is the third ingredient and there are five different sugar sources. Here's a breakdown and a couple of cleaner alternatives." },
    ],
    systemNote: "Food scan · Ultra-processed: YES · Misleading claim: 'made with whole grains' · Scan page sent",
  },
  {
    id: 'reach-out',
    title: 'A coach that reaches out',
    copy:
      "mosaime doesn't wait for you to start every conversation. Expect recipe suggestions aligned to your goals, workout reminders, and proactive check-ins — the kind of nudge that keeps you on track without feeling like a chore.",
    messages: [
      { from: 'coach', text: "You've been a bit light on vegetables this week. Here's a quick dinner that'd help: lemon herb salmon with roasted broccoli and quinoa — about 25 minutes. Want the recipe?" },
      { from: 'user', text: 'Yes please.' },
    ],
    systemNote: 'Proactive suggestion sent · Type: recipe · Aligned to: plant-forward goal',
  },
];

function FeatureChatThread({ block }: { block: FeatureBlock }) {
  return (
    <div className="hiw-chat-slider">
      <div className="hiw-chat-thread">
        {block.messages.map((message, i) => (
          <div
            key={`${block.id}-${i}`}
            className={`hiw-chat-bubble ${message.from === 'user' ? 'hiw-chat-user' : 'hiw-chat-ai'}`}
          >
            {message.text}
          </div>
        ))}
      </div>
      <div className="hiw-chat-system-note">
        <span className="hiw-system-label">&#9881; System</span>
        {block.systemNote}
      </div>
    </div>
  );
}

const onboardingSteps = [
  {
    title: 'Sign up and verify your phone',
    body:
      "Enter your name, email, and US phone number. You'll receive a 6-digit verification code by text — once confirmed, you're in. Your first 7 days are free.",
    optional: false,
  },
  {
    title: 'Answer a short intake survey',
    body:
      'About 15 questions covering your schedule, diet preferences, what kinds of movement you enjoy, and your wellness goals. This is how mosaime learns enough to be genuinely useful from your very first text — not a generic chatbot starting from zero.',
    optional: false,
  },
  {
    title: 'Build a workout plan',
    body:
      "Tell mosaime what kinds of movement you enjoy, how many days a week you want to train, and your experience level — it'll build a plan and deliver daily workout links by text. You can skip this and come back to it anytime.",
    optional: true,
  },
  {
    title: 'Your first text arrives',
    body:
      'Within minutes of finishing setup, mosaime reaches out with a greeting and your first coaching brief. From here, you just reply naturally.',
    optional: false,
  },
];

const timelineItems = [
  {
    time: '~8am',
    title: 'Morning greeting and coaching brief',
    body:
      'Your day starts with a text: a sleep check-in, a note about the weather, and a link to your daily coaching brief. The brief covers your coaching focus for the day, habit reminders, today\'s workout if you have one, and anything coming up on your cooking or shopping agenda.',
    variant: 'scheduled' as const,
  },
  {
    time: '~10am',
    title: 'Mid-morning check-in',
    body:
      "A light nudge to log breakfast if you haven't, plus one question to learn you a bit better — phrased like a coach getting to know a client, not a survey.",
    variant: 'scheduled' as const,
  },
  {
    time: '~1pm',
    title: 'Lunch nudge',
    body: 'A prompt to log lunch, and a step-count check-in if you have Fitbit connected.',
    variant: 'scheduled' as const,
  },
  {
    time: 'Anytime',
    title: 'Text anytime',
    body:
      'Log a meal, describe a workout, share how you\'re feeling, send a photo of your lunch or an ingredient label. mosaime responds in real time with coaching grounded in your goals and history.',
    variant: 'standing' as const,
  },
  {
    time: '~6–8pm',
    title: 'Evening wind-down',
    body: 'A link to your daily nutrition summary and a nudge to wrap up your log for the day.',
    variant: 'scheduled' as const,
  },
  {
    time: 'Sundays',
    title: 'Weekly summary and Wellness Mosaic',
    body:
      'A recap of the week — meals and workouts logged, goal progress, habit completion, trends vs. the week before — plus your Wellness Mosaic: one image showing how your whole week looked across diet, exercise, and mind.',
    variant: 'weekly' as const,
  },
];

export default function HowItWorks() {
  const [chartFrames, setChartFrames] = useState<string[]>(['/test_chart_300.png']);
  const [activeChartIndex, setActiveChartIndex] = useState(0);
  const [fadingChart, setFadingChart] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadFrames = async () => {
      const checks = chartFrameCandidates.map(
        (src) =>
          new Promise<string | null>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(src);
            img.onerror = () => resolve(null);
            img.src = src;
          })
      );
      const loadedFrames = (await Promise.all(checks)).filter((src): src is string => Boolean(src));

      if (!isMounted) {
        return;
      }

      if (loadedFrames.length > 0) {
        setChartFrames(loadedFrames);
      }
    };

    loadFrames();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setActiveChartIndex(0);
  }, [chartFrames]);

  useEffect(() => {
    if (chartFrames.length < 2) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setFadingChart(true);
      setTimeout(() => {
        setActiveChartIndex((prev) => (prev + 1) % chartFrames.length);
        setFadingChart(false);
      }, 400);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [chartFrames]);

  return (
    <div className="landing-page">
      <PublicHeader />

      {/* ---- HERO ---- */}
      <section className="hiw-hero">
        <div className="landing-container">
          <h1>How mosaime works.</h1>
          <p className="hiw-hero-sub">
            mosaime is a wellness coach that lives entirely in your text messages — no app, no
            dashboard, no friction. Here's how to get started, what a typical day looks like, and
            what you can do by text.
          </p>
        </div>
      </section>

      {/* ---- GETTING STARTED ---- */}
      <section className="section hiw-getting-started">
        <div className="landing-container">
          <h2 className="section-title">Get started today.</h2>
          <ol className="hiw-steps">
            {onboardingSteps.map((step, i) => (
              <li key={i} className={`hiw-step ${step.optional ? 'hiw-step-optional' : ''}`}>
                <div className="hiw-step-num">{i + 1}</div>
                <div className="hiw-step-body">
                  <h3>
                    {step.title}
                    {step.optional && <span className="hiw-step-badge">optional</span>}
                  </h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---- A DAY WITH MOSAIME ---- */}
      <section className="section hiw-day-section">
        <div className="landing-container">
          <h2 className="section-title">A day with mosaime.</h2>
          <p className="section-sub">
            mosaime reaches out at key points throughout the day. You control how much it messages
            you — but here's what a typical day looks like.
          </p>
          <ol className="hiw-timeline">
            {timelineItems.map((item, i) => (
              <li key={i} className={`hiw-timeline-item hiw-timeline-${item.variant}`}>
                <div className="hiw-timeline-time">{item.time}</div>
                <div className="hiw-timeline-content">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---- THE MOSAIC ---- */}
      <section className="section hiw-chart-section">
        <div className="landing-container hiw-chart-container">
          <div className="hiw-chart-visual">
            <div className="hiw-chart-sequence">
              <img
                src={chartFrames[activeChartIndex]}
                alt="Wellness Mosaic chart progression"
                className={`hiw-chart-img ${fadingChart ? 'hiw-chart-fade-out' : 'hiw-chart-fade-in'}`}
              />
              <div className="hiw-chart-label">{chartLabels[activeChartIndex] ?? ''}</div>
            </div>
            <div className="hiw-slider-dots" role="tablist" aria-label="Wellness chart progression frames">
              {chartFrames.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`hiw-slider-dot ${index === activeChartIndex ? 'active' : ''}`}
                  aria-label={`View chart frame ${index + 1}`}
                  aria-selected={index === activeChartIndex}
                  onClick={() => { setFadingChart(false); setActiveChartIndex(index); }}
                />
              ))}
            </div>
          </div>
          <div className="hiw-chart-text">
            <h2>One chart. The whole picture.</h2>
            <p>
              Most wellness apps give you 20 charts and 100 numbers. After a few weeks of obsessively
              tracking all of it, most people burn out and quit — having missed the forest for the
              trees.
            </p>
            <p>
              mosaime tracks everything under the hood — calories, macros, sleep quality, stress,
              workout performance, mood — but shows you one thing: your Wellness Mosaic. Every tile
              is a choice you made across diet, exercise, and mind. Deeper green reflects stronger
              decisions. Lighter tiles show where there's room to grow. The goal isn't a perfect
              score. It's watching the whole picture consistently move in the right direction.
            </p>
            <p className="hiw-chart-link">
              <Link to="/philosophy">
                The coaching behind the mosaic is grounded in a published wellness philosophy. →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ---- WHAT YOU CAN DO ---- */}
      <section className="section hiw-showcase-section">
        <div className="landing-container">
          <div className="hiw-section-label">Features</div>
          <h2 className="section-title">What you can do by text.</h2>
        </div>
        {featureBlocks.map((block, i) => (
          <div
            key={block.id}
            className={`hiw-feature-block ${i % 2 !== 0 ? 'hiw-feature-reversed' : ''}`}
          >
            <div className="landing-container hiw-feature-container">
              <div className="hiw-feature-text">
                <h3>{block.title}</h3>
                <p>{block.copy}</p>
              </div>
              <div className="hiw-feature-visual">
                <FeatureChatThread block={block} />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ---- GOALS AND HABITS ---- */}
      <section className="section hiw-goals-section">
        <div className="landing-container">
          <h2 className="section-title">Coaching toward real goals — not just tracking what you log.</h2>
          <p className="section-sub">
            mosaime doesn't just record whatever you happen to mention. It works with you toward a
            defined structure of goals and habits, all grounded in a real wellness philosophy.
          </p>
          <div className="hiw-goals-grid">
            <div className="hiw-goal-card">
              <h3>Goals</h3>
              <p>
                mosaime organizes coaching around 15 focus areas across diet, mind, and exercise —
                things like eating more whole foods, building a consistent sleep schedule, and
                finding a way to move that you'll actually enjoy. These are the throughlines every
                reply and nudge connects back to.
              </p>
            </div>
            <div className="hiw-goal-card">
              <h3>Habits</h3>
              <p>
                For each focus area, mosaime suggests specific recurring habits from a curated list
                — with schedules, reminders, and tracking. You can see your streaks and momentum
                build over time, not just a vague sense of "doing better."
              </p>
            </div>
            <div className="hiw-goal-card">
              <h3>Microhabits</h3>
              <p>
                Alongside tracked habits, mosaime surfaces lighter in-the-moment tips — small enough
                that logging them would be more hassle than help. You can turn any into a simple text
                reminder. The point is the nudge, not the record.
              </p>
            </div>
          </div>
          <p className="hiw-goals-link">
            <Link to="/features">See everything mosaime can do →</Link>
          </p>
        </div>
      </section>

      {/* ---- FITBIT ---- */}
      <section className="section hiw-philosophy-section">
        <div className="landing-container hiw-fitbit-inner">
          <h2 className="section-title">Connect Fitbit for deeper insights.</h2>
          <p>
            Link your Fitbit and mosaime automatically pulls in your sleep patterns and daily
            activity — steps, active minutes, and heart rate. Your coach uses this data to give more
            grounded advice, so recommendations reflect how you actually moved and rested, not just
            what you reported.
          </p>
          <p>
            No Fitbit? No problem. Everything works without it — Fitbit just adds passive, automatic
            tracking on top of what you log by text.
          </p>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="section cta-section">
        <div className="landing-container cta-inner">
          <h2>Ready to build your mosaic?</h2>
          <p className="cta-sub">Set up in 5 minutes. Your first 7 days are free.</p>
          <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
          <p className="cta-fine-print">
            7-day free trial · Cancel anytime · SMS rates from your carrier may apply
          </p>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
